/**
 * Separator between parts of key in LocalStorage
 * @constant {string}
 */
const LS_SEPARATOR = '__';

/**
 * A prefix to use when storing notifications read status in localStorage.
 * @constant {string}
 */
const NOTIFICATIONS_LS_PREFIX = 'CBC_NOTIFICATIONS' + LS_SEPARATOR;

/**
 * The key to use when tracking whether old read statuses have been converted.
 * @constant {string}
 */
const NOTIFICATIONS_CLEANUP_KEY = 'CBC_NOTIFICATIONS_CLEANUP';

/**
 * The pattern to match a GUID.
 * @constant {RegExp}
 */
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * A class representing storage for notifications read status.
 * Values in localStorage should be 'unread' or non exist (on reading service deleting 'unread' value from ls)
 */
class NotificationsLocalStorage {
	/**
	 * Get the read status of a notification with the specified ID.
	 * @param {string} userId - The ID of the current user.
	 * @param {string} messageId - The ID of the notification to check.
	 * @returns {APS.ReadStatus} - The read status of the notification ('read' or 'unread').
	 */
	public getReadStatus(userId: string, messageId: string): APS.ReadStatus {
		return window.localStorage.getItem(this.getLSId(userId, messageId)) === 'unread' ? 'unread' : 'read';
	}

	/**
	 * Set the read status of a notification with the specified ID.
	 * @param {string} userId - The ID of the current user.
	 * @param {string} messageId - The ID of the notification to update.
	 * @param {APS.ReadStatus} status - The new read status of the notification ('read' or 'unread').
	 * @returns {APS.ReadStatus} - The read status of the notification after the update ('read' or 'unread').
	 */
	public setReadStatus(userId: string, messageId: string, status: APS.ReadStatus): APS.ReadStatus {
		if (!userId) {
			// eslint-disable-next-line no-console
			console.log('No user for saving notifications status'); // There are no useful notifications in public marketplace
			return 'read';
		}

		if (status === 'unread') {
			window.localStorage.setItem(this.getLSId(userId, messageId), 'unread');
		} else {
			window.localStorage.removeItem(this.getLSId(userId, messageId));
		}
		return this.getReadStatus(userId, messageId);
	}

	/**
	 * Mark all notifications as read by setting their read status to 'read'(remove them).
	 */
	public markAllAsRead(userId: string): void {
		this.processLSEntities((key: string) => {
			if (key.startsWith(NOTIFICATIONS_LS_PREFIX)) {
				const splittedKey = key.split(NOTIFICATIONS_LS_PREFIX)[1].split(LS_SEPARATOR);
				const msgUserId = splittedKey[0];
				const messageId = splittedKey[1];
				if (msgUserId === userId) {
					this.setReadStatus(userId, messageId, 'read');
				}
			}
		});
	}

	/**
	 * Convert old notification read statuses to the new format by setting their read status in localStorage
	 * and removing the old item. Run only once then flag is set up.
	 */
	public convertOldReadStatusesOnce(userId: string): void {
		const cleanupDone = window.localStorage.getItem(NOTIFICATIONS_CLEANUP_KEY);
		if (!cleanupDone) {
			this.processLSEntities((key: string) => {
				if (GUID_PATTERN.test(key)) {
					const oldItem = window.localStorage.getItem(key);
					if (oldItem === 'unread' || oldItem === 'read') {
						this.setReadStatus(userId, key, oldItem);
						window.localStorage.removeItem(key);
					}
				}
			});

			window.localStorage.setItem(NOTIFICATIONS_CLEANUP_KEY, String(true));
		}
	}

	/**
	 * Call the specified function for each localStorage entity.
	 * @param {function} processFunc - The function to call for each localStorage entity.
	 */
	protected processLSEntities(processFunc: (key: string) => void) {
		const allKeys = Object.keys(localStorage);
		allKeys.forEach(processFunc);
	}

	/**
	 * Get the localStorage ID to use for the specified notification ID.
	 * @param {string} id - The notification ID to get the localStorage ID for.
	 * @returns {string} - The localStorage ID for the specified notification ID.
	 */
	protected getLSId(userId: string, id: string) {
		return NOTIFICATIONS_LS_PREFIX + userId + '__' + id;
	}
}
const notificationsLS = new NotificationsLocalStorage();
export default notificationsLS;
